import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { trackEvent } from '../utils/analytics';
import {
  BLOG_RECOMMENDATIONS,
  HOME_RECOMMENDATIONS,
  ROUTE_COLORS,
  ROUTE_DESCRIPTIONS,
  ROUTE_GROUPS,
  ROUTE_ICONS,
  ROUTE_LABELS,
  ROUTE_RECOMMENDATION_OVERRIDES,
} from '../data/routeMetadata';

const unique = (values) => [...new Set(values)];

const getGroupKey = (pathname) => {
  return Object.entries(ROUTE_GROUPS).find(([, routes]) => routes.includes(pathname))?.[0] || null;
};

const getRecommendations = (pathname) => {
  if (pathname === '/') {
    return HOME_RECOMMENDATIONS;
  }

  if (ROUTE_RECOMMENDATION_OVERRIDES[pathname]) {
    return ROUTE_RECOMMENDATION_OVERRIDES[pathname];
  }

  if (pathname === '/blog' || pathname.startsWith('/blog/')) {
    return BLOG_RECOMMENDATIONS;
  }

  const groupKey = getGroupKey(pathname);
  if (groupKey) {
    return ROUTE_GROUPS[groupKey];
  }

  return ['/emi', '/sip', '/income-tax', '/general-bill'];
};

const getRecommendationReason = (sourcePath, targetPath) => {
  if (sourcePath === '/' || sourcePath === '/blog' || sourcePath.startsWith('/blog/')) {
    return 'Popular with paisapyara users exploring related tools.';
  }

  const sourceGroup = getGroupKey(sourcePath);
  const targetGroup = getGroupKey(targetPath);

  if (sourcePath === '/emi' && targetPath === '/loan-prepayment') {
    return 'Useful if you want to reduce interest cost after seeing your EMI.';
  }

  if (sourcePath === '/emi' && targetPath === '/home-loan-eligibility') {
    return 'Helpful when you want to check affordability beyond monthly EMI.';
  }

  if (sourcePath === '/sip' && targetPath === '/goal-sip') {
    return 'Good next step if you want to map SIP investing to a target goal.';
  }

  if (sourcePath === '/income-tax' && targetPath === '/elss') {
    return 'Relevant if you are exploring tax-saving investment options.';
  }

  if (sourcePath === '/gst' && (targetPath === '/invoice' || targetPath === '/quotation')) {
    return 'Useful if you also need GST-ready commercial documents.';
  }

  if (sourcePath === '/rent-vs-buy' && targetPath === '/rental-yield') {
    return 'Helpful if you want to assess income yield on property ownership.';
  }

  if (sourceGroup && sourceGroup === targetGroup) {
    return 'Recommended because it belongs to the same planning category.';
  }

  if (sourceGroup === 'tax' && targetGroup === 'business') {
    return 'Relevant if your calculations lead to billing or document needs.';
  }

  if (sourceGroup === 'business' && targetGroup === 'tax') {
    return 'Useful for checking tax impact alongside business documents.';
  }

  if (sourceGroup === 'loans' && targetGroup === 'realEstate') {
    return 'Helpful if your loan planning connects to a property decision.';
  }

  if (sourceGroup === 'realEstate' && targetGroup === 'loans') {
    return 'Relevant if your property decision depends on financing options.';
  }

  if (sourceGroup === 'investing' && targetGroup === 'retirement') {
    return 'Useful for linking wealth-building decisions to long-term goals.';
  }

  return 'Related tool often used alongside this page.';
};

const buildCards = (pathname) => {
  return unique(getRecommendations(pathname))
    .filter((route) => route !== pathname)
    .slice(0, 3)
    .map((route) => ({
      path: route,
      title: ROUTE_LABELS[route] || route,
      description: ROUTE_DESCRIPTIONS[route] || 'Explore another helpful paisapyara tool.',
      reason: getRecommendationReason(pathname, route),
      Icon: ROUTE_ICONS[route] || ROUTE_ICONS['/blog'],
      color: ROUTE_COLORS[route] || '#2563eb',
    }));
};

const getHeading = (pathname) => {
  if (pathname === '/') {
    return 'Popular Recommendations';
  }

  if (pathname === '/blog' || pathname.startsWith('/blog/')) {
    return 'Recommended Tools';
  }

  return 'You May Also Like';
};

const RouteRecommendationSection = () => {
  const { pathname } = useLocation();
  const cards = buildCards(pathname);

  const handleRecommendationClick = (targetPath) => {
    trackEvent('recommendation_click', {
      source_path: pathname,
      target_path: targetPath,
      target_label: ROUTE_LABELS[targetPath] || targetPath,
    });
  };

  if (cards.length === 0) {
    return null;
  }

  return (
    <section className="info-box seo-section">
      <h4>{getHeading(pathname)}</h4>
      <div className="recommendation-grid">
        {cards.map((card) => (
          <Link
            key={card.path}
            to={card.path}
            onClick={() => handleRecommendationClick(card.path)}
            className="recommendation-card"
          >
            <div
              className="recommendation-card-icon"
              style={{ background: `linear-gradient(135deg, ${card.color}, ${card.color}dd)` }}
            >
              <card.Icon />
            </div>
            <div className="recommendation-card-title">{card.title}</div>
            <p className="recommendation-card-description">{card.description}</p>
            <p className="recommendation-card-reason">Why this is recommended: {card.reason}</p>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default RouteRecommendationSection;

